import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiURL from '../Api/Api';
import SuperAdminSidebar from './SuperAdminSidebar';
import SuperAdminOverview from './SuperAdminOverview';
import SuperAdminCompanies from './SuperAdminCompanies';
import SuperAdminPackages from './SuperAdminPackages';
import SuperAdminPackageForm from './SuperAdminPackageForm';
import SuperAdminUsers from './SuperAdminUsers';
import { emptyPackageForm } from './SuperAdminShared';
import SuperAdminChatHelp from './SuperAdminChatHelp';
import SuperAdminIncidents from './SuperAdminIncidents';
import SuperAdminCoupons from './SuperAdminCoupons';

const emptyCouponForm = {
  id: null,
  coupon_code: '',
  title: '',
  description: '',
  visibility: 'public',
  discount_type: 'percentage',
  discount_value: '',
  package_scope: 'all',
  package_id: '',
  start_date: '',
  end_date: '',
  usage_limit: '',
  status: 'active',
};

const parseFormNumber = (value) => {
  if (value === null || value === undefined) return NaN;
  const normalized = String(value).replace(/,/g, '').trim();
  if (normalized === '') return NaN;
  return Number(normalized);
};

const SuperAdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    total_companies: 0,
    active_companies: 0,
    active_packages: 0,
    top_package: null,
  });
  const [companies, setCompanies] = useState([]);
  const [packages, setPackages] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [packageForm, setPackageForm] = useState(emptyPackageForm);
  const [isSavingPackage, setIsSavingPackage] = useState(false);
  const [couponForm, setCouponForm] = useState(emptyCouponForm);
  const [isSavingCoupon, setIsSavingCoupon] = useState(false);
  const [superAdminForm, setSuperAdminForm] = useState({
    user_name: '',
    password: '',
    confirm_password: '',
    phone: '',
    email: '',
  });
  const [isSavingSuperAdmin, setIsSavingSuperAdmin] = useState(false);

  const isNewPackageRoute = location.pathname.toLowerCase() === '/superadmin/packages/new';
  const editPackageMatch = location.pathname.match(/^\/superadmin\/packages\/([^/]+)\/edit$/i);
  const editPackageId = editPackageMatch?.[1] || null;
  const isPackageFormRoute = isNewPackageRoute || Boolean(editPackageId);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      const [dashboardResponse, packagesResponse, couponsResponse] = await Promise.all([
        fetch(`${ApiURL}/superadmin_get_dashboard.php`, { credentials: 'include' }),
        fetch(`${ApiURL}/superadmin_get_packages.php`, { credentials: 'include' }),
        fetch(`${ApiURL}/superadmin_get_coupons.php`, { credentials: 'include' }),
      ]);

      const dashboardData = await dashboardResponse.json();
      const packageData = await packagesResponse.json();
      const couponData = await couponsResponse.json();

      if (dashboardData.status !== 'success') {
        throw new Error(dashboardData.message || 'Failed to load dashboard');
      }
      if (packageData.status !== 'success') {
        throw new Error(packageData.message || 'Failed to load packages');
      }
      if (couponData.status !== 'success') {
        throw new Error(couponData.message || 'Failed to load coupons');
      }

      setStats(dashboardData.stats || {});
      setCompanies(dashboardData.companies || []);
      setPackages(packageData.packages || []);
      setCoupons(couponData.coupons || []);
      setSuperAdmins(dashboardData.super_admins || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load super admin data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, []);

  useEffect(() => {
    if (!isPackageFormRoute) return;

    setActiveTab('packages');

    if (isNewPackageRoute) {
      setPackageForm(emptyPackageForm);
      return;
    }

    const pkg = packages.find((item) => String(item.id) === String(editPackageId));
    if (!pkg) return;

    setPackageForm({
      id: pkg.id,
      package_name: pkg.package_name || '',
      price: pkg.price || '',
      duration_months: pkg.duration_months || '',
      max_users: pkg.max_users || '',
      max_admins: pkg.max_admins || '',
      max_workers: pkg.max_workers || '',
      features: pkg.features || '',
      status: pkg.status || 'active',
    });
  }, [editPackageId, isNewPackageRoute, isPackageFormRoute, packages]);

  const showPackages = () => {
    setActiveTab('packages');
    navigate('/superadmin');
  };

  const openCreatePackage = () => {
    setPackageForm(emptyPackageForm);
    navigate('/superadmin/packages/new');
  };

  const openEditPackage = (pkg) => {
    setPackageForm({
      id: pkg.id,
      package_name: pkg.package_name || '',
      price: pkg.price || '',
      duration_months: pkg.duration_months || '',
      max_users: pkg.max_users || '',
      max_admins: pkg.max_admins || '',
      max_workers: pkg.max_workers || '',
      features: pkg.features || '',
      status: pkg.status || 'active',
    });
    navigate(`/superadmin/packages/${pkg.id}/edit`);
  };

  const savePackage = async () => {
    const packageName = String(packageForm.package_name || '').trim();
    const price = parseFormNumber(packageForm.price);
    const durationMonths = parseFormNumber(packageForm.duration_months);
    const adminLimit = parseFormNumber(packageForm.max_admins || 0);
    const workerLimit = parseFormNumber(packageForm.max_workers || 0);
    const totalSeats = adminLimit + workerLimit;

    if (!packageName || !Number.isFinite(price) || price <= 0 || !Number.isFinite(durationMonths) || durationMonths <= 0) {
      toast.error('Package name, price, and duration are required');
      return;
    }

    if (!Number.isFinite(adminLimit) || adminLimit < 0 || !Number.isFinite(workerLimit) || workerLimit < 0) {
      toast.error('Admin and worker seats must be valid numbers');
      return;
    }

    if (totalSeats <= 0) {
      toast.error('Add at least one admin or worker seat');
      return;
    }

    setIsSavingPackage(true);
    try {
      const url = packageForm.id
        ? `${ApiURL}/superadmin_update_package.php`
        : `${ApiURL}/superadmin_create_package.php`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...packageForm,
          package_name: packageName,
          price,
          duration_months: durationMonths,
          max_users: totalSeats,
          max_admins: adminLimit,
          max_workers: workerLimit,
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error ${response.status}`);
      }

      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to save package');
      }

      toast.success(packageForm.id ? 'Package updated' : 'Package created');
      setPackageForm(emptyPackageForm);
      showPackages();
      refreshData();
    } catch (error) {
      toast.error(error.message || 'Failed to save package');
    } finally {
      setIsSavingPackage(false);
    }
  };

  const saveSuperAdmin = async () => {
    const userName = superAdminForm.user_name.trim();
    const password = superAdminForm.password;
    const confirmPassword = superAdminForm.confirm_password;

    if (!userName || !password || !confirmPassword) {
      toast.error('Username and password are required');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Password and confirm password must match');
      return;
    }

    setIsSavingSuperAdmin(true);
    try {
      const response = await fetch(`${ApiURL}/superadmin_create_user.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_name: userName,
          password,
          phone: superAdminForm.phone.trim(),
          email: superAdminForm.email.trim(),
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to create super admin');
      }

      toast.success('Super admin created');
      setSuperAdminForm({
        user_name: '',
        password: '',
        confirm_password: '',
        phone: '',
        email: '',
      });
      refreshData();
    } catch (error) {
      toast.error(error.message || 'Failed to create super admin');
    } finally {
      setIsSavingSuperAdmin(false);
    }
  };

  const editCoupon = (coupon) => {
    setCouponForm({
      id: coupon.id,
      coupon_code: coupon.coupon_code || '',
      title: coupon.title || '',
      description: coupon.description || '',
      visibility: coupon.visibility || 'public',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_type === 'free' ? '' : coupon.discount_value || '',
      package_scope: coupon.package_scope || 'all',
      package_id: coupon.package_id || '',
      start_date: coupon.start_date || '',
      end_date: coupon.end_date || '',
      usage_limit: coupon.usage_limit || '',
      status: coupon.status || 'active',
    });
    setActiveTab('coupons');
  };

  const saveCoupon = async () => {
    const code = couponForm.coupon_code.trim().toUpperCase();
    const title = couponForm.title.trim();
    const discountValue = parseFormNumber(couponForm.discount_value);
    const usageLimit = String(couponForm.usage_limit || '').trim() === '' ? 0 : parseFormNumber(couponForm.usage_limit);

    if (!code || !title) {
      toast.error('Coupon code and title are required');
      return;
    }
    if (couponForm.discount_type !== 'free' && (!Number.isFinite(discountValue) || discountValue <= 0)) {
      toast.error('Discount value must be greater than zero');
      return;
    }
    if (couponForm.discount_type === 'percentage' && discountValue > 100) {
      toast.error('Percentage discount cannot be more than 100');
      return;
    }
    if (couponForm.package_scope === 'package' && !couponForm.package_id) {
      toast.error('Select the package for this coupon');
      return;
    }
    if (!Number.isFinite(usageLimit) || usageLimit < 0) {
      toast.error('Usage limit must be zero or a positive number');
      return;
    }

    setIsSavingCoupon(true);
    try {
      const response = await fetch(`${ApiURL}/superadmin_save_coupon.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...couponForm,
          coupon_code: code,
          title,
          discount_value: couponForm.discount_type === 'free' ? 100 : discountValue,
          usage_limit: usageLimit,
          package_id: couponForm.package_scope === 'package' ? Number(couponForm.package_id) : null,
        }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to save coupon');
      }
      toast.success(couponForm.id ? 'Coupon updated' : 'Coupon created');
      setCouponForm(emptyCouponForm);
      refreshData();
    } catch (error) {
      toast.error(error.message || 'Failed to save coupon');
    } finally {
      setIsSavingCoupon(false);
    }
  };

  const archivePackage = async (pkg) => {
    const confirmed = window.confirm(`Archive package "${pkg.package_name}"?`);
    if (!confirmed) return;

    try {
      const response = await fetch(`${ApiURL}/superadmin_delete_package.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: pkg.id }),
      });
      const data = await response.json();
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to archive package');
      }
      toast.success('Package archived');
      refreshData();
    } catch (error) {
      toast.error(error.message || 'Failed to archive package');
    }
  };

  const renderContent = () => {
    if (isPackageFormRoute) {
      return (
        <SuperAdminPackageForm
          packageForm={packageForm}
          setPackageForm={setPackageForm}
          isSavingPackage={isSavingPackage}
          onCancel={showPackages}
          onSave={savePackage}
        />
      );
    }

    if (isLoading) {
      return <div className="py-20 text-center text-sm text-slate-500">Loading super admin data...</div>;
    }

    if (activeTab === 'companies') {
      return <SuperAdminCompanies companies={companies} />;
    }

    if (activeTab === 'packages') {
      return (
        <SuperAdminPackages
          packages={packages}
          onCreatePackage={openCreatePackage}
          onEditPackage={openEditPackage}
          onArchivePackage={archivePackage}
        />
      );
    }

    if (activeTab === 'coupons') {
      return (
        <SuperAdminCoupons
          coupons={coupons}
          packages={packages}
          form={couponForm}
          setForm={setCouponForm}
          isSaving={isSavingCoupon}
          onSave={saveCoupon}
          onEdit={editCoupon}
          onReset={() => setCouponForm(emptyCouponForm)}
        />
      );
    }

    if (activeTab === 'superadmins') {
      return (
        <SuperAdminUsers
          superAdmins={superAdmins}
          form={superAdminForm}
          setForm={setSuperAdminForm}
          isSaving={isSavingSuperAdmin}
          onSave={saveSuperAdmin}
        />
      );
    }

    if (activeTab === 'chathelp') {
      return <SuperAdminChatHelp />;
    }

    if (activeTab === 'incidents') {
      return <SuperAdminIncidents />;
    }

    return <SuperAdminOverview stats={stats} companies={companies} />;
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#cffafe,transparent_34%),linear-gradient(135deg,#f8fafc,#e2e8f0)] text-slate-900">
      <div className="flex min-h-screen flex-col lg:flex-row">
        <SuperAdminSidebar
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            if (isPackageFormRoute) {
              navigate('/superadmin');
            }
          }}
        />

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white text-slate-950 shadow-xl">
            <div className="bg-[radial-gradient(circle_at_20%_20%,rgba(34,211,238,0.18),transparent_34%),radial-gradient(circle_at_82%_12%,rgba(251,191,36,0.14),transparent_24%)] px-6 py-6 sm:px-8">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.28em] text-cyan-700">Algo2botTxn Control Center</div>
                  <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">Super admin workspace</h2>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                    Manage companies and packages from one clean page. Package seats are split only into admins and workers, and total users are calculated automatically.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={refreshData}
                  className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-300 transition hover:-translate-y-0.5 hover:bg-slate-800"
                >
                  Refresh Data
                </button>
              </div>
            </div>
          </div>

          {renderContent()}
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
